/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable import/extensions */
/** @import {Mode, Point, Voice} from './context.js' */
import React, { useMemo, useState } from "react";
import { withI18n } from "@lingui/react";
import { t } from "@lingui/macro";
import { Helmet } from "react-helmet";
import useCurrentLanguage from "../../functions/languages/useCurrentLanguage.js";
import { getBaseUrl } from "../../functions/getBaseUrl";
import Root from "../../components/_Root";
import sendEvent, { sendFacebookEvent } from "../../functions/analytics.js";
import logo from "./assets/logo.svg";
import logoRu from "./assets/logoRu.svg";
import { DEFAULT_AREA_RADIUS } from "./constants";
import { OnDemandContext } from "./context";
import { DesktopControls } from "./Controls";
import { generateTourUrl } from "./lib";
import { MapScreen } from "./MapScreen";
import TourParametersScreen from "./TourParametersScreen";
import { WelcomePopup } from "./WelcomePopup";

import "./OnDemandWidget.css";
import { useMediaQuery } from "beautiful-react-hooks";
import isSSR from "../../constants/env/ssr.js";

function OnDemandWidget() {
  /** @type {[Mode, React.Dispatch<React.SetStateAction<Mode>>]} */
  const [mode, setMode] = useState("radius");
  /** @type {[Point | null, React.Dispatch<React.SetStateAction<Point | null>>]} */
  const [startingPoint, setStartingPoint] = useState(null);
  /** @type {[Point | null, React.Dispatch<React.SetStateAction<Point | null>>]} */
  const [endingPoint, setEndgingPoint] = useState(null);
  const [areaRadius, setAreaRadius] = useState(1500);
  const [step, setStep] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  /** @type {[Voice | null, React.Dispatch<React.SetStateAction<Voice | null>>]} */
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("manual");
  const lang = useCurrentLanguage();
  const calculatedSize = lang === "ru" ? 45 : 40;
  /**
   * @param {Point} [newStartingPoint=null]
   */
  const resetTourGeography = (newStartingPoint = null) => {
    setStartingPoint(newStartingPoint);
    setEndgingPoint(null);
    setAreaRadius(DEFAULT_AREA_RADIUS);
  };

  /**
   * @param {Mode} newMode
   */
  const handleModeChange = newMode => {
    resetTourGeography(startingPoint);
    setMode(newMode);
  };

  const nextStepAvailable = useMemo(() => {
    switch (step) {
      case 0:
        if (mode === "radius") return Boolean(startingPoint && areaRadius);
        else if (mode === "path") return Boolean(startingPoint && endingPoint);
        break;
      case 1:
        return Boolean(selectedLanguage && selectedVoice);
      default:
        return false;
    }
  }, [step, mode, startingPoint, endingPoint, areaRadius, selectedLanguage, selectedVoice]);

  const prevStepAvailable = useMemo(() => {
    if (step === 0) {
      return false;
    }

    return true;
  }, [step]);

  const isMobile = !isSSR && useMediaQuery("(max-width: 767px)");

  const goToNextStep = () => {
    /* После экрана с выбором параметров тура перенаправляем на основной сайт */
    if (step === 1) {
      const tourParams = {
        language: selectedLanguage,
        voice: selectedVoice.id,
        audioProvider: selectedVoice.provider,
        selectedPlaces,
        selectedMethod,
        startingPoint: {
          latitude: startingPoint.lat,
          longitude: startingPoint.lng,
        },
      };

      if (mode === "path") {
        tourParams.endingPoint = {
          latitude: endingPoint.lat,
          longitude: endingPoint.lng,
        };
      } else if (mode === "radius") {
        tourParams.radius = areaRadius;
      }

      const tourUrl = generateTourUrl(tourParams);
      sendEvent("track", "on_demand_tour_params_next_click");
      return window.open(tourUrl, "_blank");
    }

    if (step === 0) {
      sendEvent("track", "on_demand_map_next_click");
      sendFacebookEvent("track", "on_demand_map_next_click");
    }

    setStep(state => state + 1);
  };

  const goToPrevStep = () => {
    if (step === 1) {
      setSelectedLanguage(null);
      setSelectedVoice(null);
      setSelectedPlaces([]);
      sendEvent("track", "on_demand_parameters_back_click");
    }

    setStep(state => state - 1);
  };

  const ProductHead = withI18n()(({ i18n }) => {
    const title = i18n._(t`The whole world with customized experiences`);
    const description = i18n._(
      t`Create tours on your own rules and discover cities like never before`,
    );
    const logoUrl =
      "https://wgt-prod-storage.s3.eu-west-3.amazonaws.com/upload/image/brand/ai-on-demand-logo.png";
    return (
      <Helmet>
        <meta property="og:image" content={logoUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="WeGoTrip" />
      </Helmet>
    );
  });

  return (
    <Root
      hideHeader
      footer={false}
      title={t`The whole world with customized experiences`}
      description={t`Create tours on your own rules and discover cities like never before`}
    >
      <OnDemandContext.Provider
        value={{
          mode,
          startingPoint,
          endingPoint,
          areaRadius,
          selectedLanguage,
          selectedVoice,
          selectedPlaces,
          selectedMethod,
          changeMode: handleModeChange,
          changeStartingPoint: setStartingPoint,
          changeEndingPoint: setEndgingPoint,
          changeAreaRadius: setAreaRadius,
          changeSelectedLanguage: setSelectedLanguage,
          changeSelectedVoice: setSelectedVoice,
          changeSelectedMethod: setSelectedMethod,
          changeSelectedPlaces: setSelectedPlaces,
          resetTourGeography,
        }}
      >
        <div className="on-demand-widget">
          <ProductHead />
          {/* {!isMobile && ( */}
          <div className="on-demand-widget__header">
            <a href={getBaseUrl()} target="_blank" rel="noopener">
              <img
                height={calculatedSize}
                title="Powered by WeGoTrip"
                src={lang === "ru" ? logoRu : logo}
              />
            </a>
          </div>
          {/* )} */}

          <div className="on-demand-widget__screen-container">
            {
              [
                <MapScreen key={0} />,
                <TourParametersScreen
                  key={1}
                  selectedPlaces={selectedPlaces}
                  setSelectedPlaces={setSelectedPlaces}
                  onClickPrev={goToPrevStep}
                />,
              ][step]
            }
          </div>

          <div className="on-demand-widget__controls-container">
            <DesktopControls
              step={step + 1}
              stepsTotal={3}
              disablePrev={!prevStepAvailable}
              disableNext={!nextStepAvailable}
              onClickNext={goToNextStep}
              onClickPrev={goToPrevStep}
            />
          </div>

          <WelcomePopup />
        </div>
      </OnDemandContext.Provider>
    </Root>
  );
}
export default OnDemandWidget;
